import { useEffect, useState } from "react";
import OptionChooser from "../../../../components/optionChooser";
import KeywordDisplayWithX from "../../../../components/piece/keywordDisplayWithX";
import SuggestionSearch from "../../../../components/suggestionSearch";
import TextArea from "../../../../components/textField/textArea";
import TextField from "../../../../components/textField/textField";
import TextFieldDropdown from "../../../../components/textField/textFieldDropdown";
import { removeLastOccurrance } from "../../../../utils/arrayUtils";
import { EditPieceStepProps } from "../dashboardEditPiece";

import TextFieldArtistSuggestions from "../../../../components/textField/textFieldArtistSuggestions";
import { useAppContext } from "../../../../context/appContext";
import defaultKeywordOptions from "../../../../data/json/keywords.json";
import { PieceDTO } from "../../../../openapi-generated-client";
import { calculateMoneyEarned, formatPrice } from "../../../../utils/priceUtils";
import { toRoman } from "../../../../utils/numberUtils";

const allPrices = ["Free"];
for(var i = 1; i <= 150; i++) {
    allPrices.push(i+"€");
}

const allDurations = ["<1min"];
for(var i = 1; i <= 300; i++) {
    allDurations.push(i + "min");
}

const allPageCounts: string[] = [];
for(let i = 0; i <= 200; i++) {
    allPageCounts.push(i.toString());
}

const allMovements = ["None"];
for(let i = 1; i <= 50; i++) {
    allMovements.push(i.toString());
}

export { allDurations, allPrices };

export default function EditPieceGeneral(props: EditPieceStepProps) {

    const {keywordController} = useAppContext();

    const [keywordSearch, setKeywordSearch] = useState("");
    const [keywordOptions, setKeywordOptions] = useState<string[]>([]);

    async function fetchKeywords() {
        await keywordController.getKeywords().then((response) => {
            const newKeywordOptions = defaultKeywordOptions;
            for(var i = 0; i < response.data.length; i++) {
                const newKeyword = response.data[i] as string;
                if(newKeywordOptions.includes(newKeyword)) continue;
                newKeywordOptions.push(newKeyword);
            }
            setKeywordOptions(newKeywordOptions);
        });
    }

    useEffect(() => {
        fetchKeywords();
    }, []);

    const allYears = [];
    for(var i = 1300; i <= new Date().getFullYear(); i++) {
        allYears.push(i.toString());
    }
    if(props.piece.year != undefined && !allYears.includes(props.piece.year.toString())) {
        allYears.push(props.piece.year.toString());
    }

    function getMessage(data: PieceDTO) {
        if(!data.title || data.title.length < 3) {
            return "Your title must be at least 3 characters long";
        }
        if(data.year != undefined && (data.year < 1000 || data.year > new Date().getFullYear())) {
            return "Your date has to be between 1300 and " + new Date().getFullYear();
        }
        if(data.keywords != undefined && data.keywords.length < 1) {
            return "Please enter at least 1 keyword that describes your piece.";
        }
        if(data.original == undefined) {
            return "Please tell us if your piece is original or a transcription";
        }
        if(data.original == false && data.transcriptionInfo != undefined && (!data.transcriptionInfo.artistId || !data.transcriptionInfo.name || data.transcriptionInfo.name?.length < 2)) {
            return "If your piece is a transcription, please provide the original composer and year";
        }
        if(data.difficulty != 1 && data.difficulty != 0) {
            return "Please choose the pieces difficulty";
        }
        if(data.externalScoreLink != undefined && data.pages == undefined) {
            return "Please select an amount of pages for your piece";
        }
        return "";
    }

    useEffect(() => {
        props.setStepMessage(getMessage(props.piece));
    }, [props.piece]);

    return <>
        <h2>General Information</h2>
        <p className="quotation">Please give us some general information about your piece.</p>
        <TextField placeholder="Title of your piece..." maxLength={70} label="Title" value={props.piece.title} onChange={(v: string) => {
            const newData = {...props.piece, title: v};
            props.setPiece(newData);
        }}/>
        <TextArea placeholder="Describe your piece..." maxLength={3000} label="Description" value={props.piece.description} onChange={(v: string) => {
            const newData = {...props.piece, description: v};
            props.setPiece(newData);
        }}/>
        <TextFieldDropdown title="Select a year" label="Year" options={allYears} active={props.piece.year.toString()} setActive={(v: string) => {
            props.setPiece({...props.piece, year: Number(v)});
        }}/>
        <OptionChooser label="Type" options={["Original", "Transcription"]} active={props.piece.original == undefined ? undefined : props.piece.original ? "Original" : "Transcription"} setActive={(v: string) => {
            if(v == "Transcription") {
                props.setPiece({...props.piece, original: false, transcriptionInfo: {
                    artistId: -1, name: "", realYear: 2000}});
            }else {
                props.setPiece({...props.piece, original: true, transcriptionInfo: undefined});
            }
        }}/>
        
        {props.piece.original == false && props.piece.transcriptionInfo ? <>
            <TextFieldArtistSuggestions artist={{
                id: props.piece.transcriptionInfo.artistId ? props.piece.transcriptionInfo.artistId : -1,
                name: props.piece.transcriptionInfo.name? props.piece.transcriptionInfo.name : ""
            }
            } setArtist={(id: number, v: string) => {
                const newData = {...props.piece, transcriptionInfo: {realYear: props.piece.transcriptionInfo?.realYear ?? 2000, artistId: id, name: v}};
                props.setPiece(newData);

            }} placeholder="Original composer..." maxLength={50} label="Original composer" value={props.piece.transcriptionInfo.name
            } onChange={(v: string) => {
                const newData = {...props.piece, transcriptionInfo: {artistId: props.piece.transcriptionInfo?.artistId ?? -1, name: v, realYear: props.piece.transcriptionInfo?.realYear ?? 2000}};
                props.setPiece(newData);
            }}/>
            <TextFieldDropdown title="Select a year" label="Original Year" options={allYears} active={props.piece.transcriptionInfo.realYear? props.piece.transcriptionInfo.realYear.toString() : ""} setActive={(v: string) => {
                props.setPiece({...props.piece, transcriptionInfo: {realYear: Number(v), artistId: props.piece.transcriptionInfo?.artistId ?? -1, name: props.piece.transcriptionInfo?.name ?? ""}});
            }}/>
        </> : <></>}
        <OptionChooser label="Difficulty" options={["Pedagogic", "Concert"]} active={props.piece.difficulty == 0 ? "Pedagogic" : props.piece.difficulty == 1 ? "Concert" : undefined} setActive={(v: string) => {
            props.setPiece({...props.piece, difficulty: v == "Pedagogic" ? 0 : v == "Concert" ? 1 : -1});
        }}/>
        <div className="txoria-text-field">
            <label>Keywords</label>
            <div className="keyword-choose-list">
                {props.piece.keywords.map(e => {
                    return <KeywordDisplayWithX name={e} onClose={() => {
                        props.setPiece({...props.piece, keywords: removeLastOccurrance(props.piece.keywords, e)});
                    }}/>;
                })}
            </div>
            <SuggestionSearch shuffle showOnFocus maxLength={25} placeholder="Search for keywords to add..." options={keywordOptions} onSearch={setKeywordSearch} onEnter={(v: string) => {
                v = v.toLowerCase();
                if(props.piece.keywords.includes(v)) return;
                props.setPiece({...props.piece, keywords: [...props.piece.keywords, v]});
            }}/>
        </div>
        <div className="movements">
            <TextFieldDropdown title="How many movements has your piece?" label="Movements" options={allMovements} active={props.piece.movements != undefined && props.piece.movements.length > 0 ? props.piece.movements.length.toString() : "None"} setActive={(v: string) => {
                if(v == "None") {
                    props.setPiece({...props.piece, movements: undefined});
                }else {
                    const newMovementCount = Number(v);
                    const oldMovements: string[] = props.piece.movements != undefined ? props.piece.movements : [];
                    const newMovements: string[] = [];
                    for(let i = 0; i < newMovementCount; i++) {
                        if(oldMovements[i]) {
                            newMovements.push(oldMovements[i]);
                        }else {
                            newMovements.push(toRoman(i+1) + " Movement");
                        }
                    }
                    props.setPiece({...props.piece, movements: newMovements.length > 0 ? newMovements : undefined});
                }
            }}/>
            <div className="movement-names">
                {props.piece.movements != undefined && props.piece.movements.length > 0 && <a className="subtitle">Enter the name of each movement</a>}
                {props.piece.movements != undefined && props.piece.movements.map((e, i) => {
                    return <input type="text" placeholder={"Name of movement " + (i+1)} value={e} onChange={(v) => {
                        const newMovements = [...props.piece.movements!];
                        newMovements[i] = v.target.value;
                        props.setPiece({...props.piece, movements: newMovements});
                    }}/>;
                })}
            </div>
        </div>
        {
            props.piece.externalScoreLink != undefined && 
            <TextFieldDropdown title="Select the page count" label="Pages" options={allPageCounts} active={props.piece.pages ? allPageCounts[props.piece.pages] : "0"} setActive={(v: string) => {
                const index = allPageCounts.indexOf(v);
                if (index !== -1) {
                    props.setPiece({...props.piece, pages: index});
                }
            }}/>

        }
        <TextFieldDropdown title="Select the duration" label="Duration" options={allDurations} active={allDurations[props.piece.duration]} setActive={(v: string) => {
            const index = allDurations.indexOf(v);
            if (index !== -1) {
                props.setPiece({...props.piece, duration: index});
            }
        }}/>
        <div className="price-info">
                <TextFieldDropdown title="Choose your price" label="Price" options={allPrices} active={allPrices[props.piece.price]} setActive={(v: string) => {
                    const index = allPrices.indexOf(v);
                    if (index !== -1) {
                        props.setPiece({...props.piece, price: index});
                    }
                }}/>
                {props.piece.externalScoreLink == undefined && 
                        <div className="info">
                            <a>{formatPrice(calculateMoneyEarned(props.piece.price))} € profit per score sold (after fees).</a>
                            <a className="subtitle">We charge 20% + 25ct every purchase to cover server maintenance and transaction costs.</a>
                        </div>
                    
                }
            </div>
    </>;

}